/**
 * Frontend Configuration Service
 * Manages customer-specific configuration with hot-reload capabilities
 */

import { migrateDevConfiguration } from '../utils/configMigration';
import { authFetch } from './authFetch';

interface CustomerConfiguration {
  customer: {
    id: string;
    name: string;
    resourceGroup: string;
  };
  ui: {
    applicationName: string;
    primaryColor: string;
    secondaryColor: string;
    logoUrl: string;
    faviconUrl?: string;
  };
  ai: {
    defaultModel?: string; // Platform-wide default model (set in AI Models tab)
    apiVersion?: string; // Platform-wide API version (set in AI Models tab)
    documentIntelligence: {
      modelId?: string;
      models?: {
        DEED?: string;
        MORTGAGE_RELEASE?: string;
        [key: string]: string | undefined;
      };
    };
    openai: {
      modelName: string;
      temperature: number;
      maxTokens: number;
      confidenceThreshold: number;
    };
    prompts: {
      ownershipTypeInference: {
        systemPrompt: string;
        userPromptTemplate: string;
        temperature: number;
        maxTokens: number;
      };
    };
    maps?: {
      addressValidation?: {
        enabled: boolean;
        endpoint: string;
        keyVaultSecretName: string;
        confidenceThreshold: number;
      };
    };
  };
  processing: {
    fieldConsolidation: {
      enabled: boolean;
      saleDate: {
        confidenceThreshold: number;
      };
      legalDescription: {
        concatenationRule: 'space_separated' | 'paragraph_breaks' | 'smart_join';
        deduplication: boolean;
        formatting: 'preserve' | 'normalize' | 'uppercase';
        useHighestConfidenceCoordinates: boolean;
      };
    };
    textFormatting: {
      enabled: boolean;
      rules: Array<{
        fieldName: string;
        case: 'preserve' | 'uppercase' | 'lowercase' | 'title_case';
        trimTrailing: string[];
        removePatterns: string[];
      }>;
    };
  };
  integration: {
    systemOfRecord: {
      apiEndpoint: string;
      authType: string;
    };
    transactionTypes?: string[];
    ownershipTypes?: string[];
  };
  limits: {
    documentsPerMonth: number;
    storageGB: number;
    maxFileSize: string;
    pricing: {
      perDocument: number;
      perGB: number;
      baseMonthly: number;
    };
  };
}

interface ConfigurationWatcher {
  callback: (config: CustomerConfiguration) => void;
}

class ConfigurationService {
  private config: CustomerConfiguration | null = null;
  private watchers: ConfigurationWatcher[] = [];
  private customerId: string;

  constructor() {
    const baseCustomerId = import.meta.env.VITE_CUSTOMER_ID;
    if (!baseCustomerId) {
      console.error('VITE_CUSTOMER_ID is not set — configuration service will not function correctly');
    }
    const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';
    const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
    
    // Create environment-specific customer ID for storage separation
    if (isDemoMode && !isDevMode) {
      this.customerId = `${baseCustomerId}-demo`;
    } else if (isDevMode) {
      this.customerId = `${baseCustomerId}-dev`;
    } else {
      this.customerId = baseCustomerId || '';
    }
    
    console.log('Configuration Service initialized for:', this.customerId);

    // Auto-migrate development configuration if needed
    if (isDevMode) {
      migrateDevConfiguration();
    }
  }

  /**
   * Get current configuration
   */
  async getConfiguration(): Promise<CustomerConfiguration> {
    if (!this.config) {
      await this.loadConfiguration();
    }
    return this.config!;
  }

  /**
   * Load configuration from API (SINGLE SOURCE OF TRUTH)
   * localStorage is ONLY used as offline cache, never as primary source
   */
  private async loadConfiguration(): Promise<void> {
    try {
      let apiConfig: CustomerConfiguration;

      // Always load from API (AUTHORITATIVE SOURCE)
      {
        const response = await authFetch('/configuration');

        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'API returned success=false');
        }

        apiConfig = data.config;
      }

      // API is the source of truth
      const oldConfig = this.config;
      this.config = apiConfig;
      
      // Cache in localStorage for offline access ONLY
      this.saveToLocalStorage(this.config);
      
      console.log('✅ Configuration loaded from API successfully');

      // Notify watchers if configuration changed
      if (JSON.stringify(oldConfig) !== JSON.stringify(this.config)) {
        this.notifyWatchers();
      }

    } catch (error) {
      console.error('❌ CRITICAL: Failed to load configuration from API:', error);
      
      // Try localStorage as emergency fallback (offline scenario)
      const cachedConfig = this.loadFromLocalStorage();
      if (cachedConfig) {
        console.warn('⚠️ Using cached configuration from localStorage (OFFLINE MODE)');
        this.config = cachedConfig;
        this.notifyWatchers();
      } else {
        // NO CONFIGURATION AVAILABLE - This is a critical failure
        console.error('❌ CRITICAL: No configuration available (API failed, no cache). Using minimal fallback.');
        this.config = this.getFallbackConfiguration();
        this.saveToLocalStorage(this.config);
        
        // Throw error to make failure visible
        throw new Error('Configuration service unavailable. Check API connection and try again.');
      }
    }
  }

  /**
   * Flatten nested objects into colon-separated keys
   * Example: { processing: { textFormatting: { enabled: true } } } 
   *       -> { "Processing:TextFormatting:Enabled": "true" }
   */
  private flattenUpdates(updates: Record<string, any>, prefix: string = ''): Record<string, any> {
    const flattened: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(updates)) {
      // Convert camelCase to PascalCase for backend
      // Special handling for acronyms like "ui" -> "UI"
      let pascalKey: string;
      if (key.toLowerCase() === 'ui') {
        pascalKey = 'UI';
      } else if (key.toLowerCase() === 'ai') {
        pascalKey = 'AI';
      } else {
        pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      }
      
      const fullKey = prefix ? `${prefix}:${pascalKey}` : pascalKey;
      
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(flattened, this.flattenUpdates(value, fullKey));
      } else {
        // Convert value to string for backend
        if (Array.isArray(value)) {
          flattened[fullKey] = JSON.stringify(value);
        } else if (typeof value === 'boolean') {
          flattened[fullKey] = value.toString();
        } else {
          flattened[fullKey] = value;
        }
      }
    }
    
    return flattened;
  }

  /**
   * Update configuration
   * @param updates - Configuration updates to apply
   */
  async updateConfiguration(updates: Record<string, any>): Promise<void> {
    try {
      // Flatten nested objects into colon-separated keys for backend
      const flattenedUpdates = this.flattenUpdates(updates);
      console.log('📤 Flattened updates for backend:', flattenedUpdates);
      
      // Try to save to API first
      {
        const response = await authFetch('/configuration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: flattenedUpdates,
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            console.log('✅ Configuration saved to API successfully');
            
            // Now reload from API to get the authoritative configuration
            console.log('🔄 Reloading configuration from API...');
            const getResponse = await authFetch('/configuration');
            
            if (getResponse.ok) {
              const getData = await getResponse.json();
              if (getData.success && getData.config) {
                console.log('✅ Configuration reloaded from API');
                const oldConfig = this.config;
                this.config = getData.config;
                
                // Save to localStorage for offline access
                if (this.config) {
                  this.saveToLocalStorage(this.config);
                }
                
                // Notify watchers
                if (JSON.stringify(oldConfig) !== JSON.stringify(this.config)) {
                  this.notifyWatchers();
                }
                return;
              }
            }
          } else if (data.error && data.error.includes('Forbidden')) {
            // Handle RBAC error
            throw new Error('Unauthorized: Admin role required to modify configuration');
          }
        } else if (response.status === 403) {
          // Handle HTTP 403 Forbidden
          throw new Error('Unauthorized: Admin role required to modify configuration');
        }
      }
      
      // Fallback: If API save/reload failed, merge locally
      console.warn('⚠️ API save/reload failed, falling back to local merge');
      if (this.config) {
        this.config = this.mergeConfig(this.config, updates);
      } else {
        this.config = this.mergeConfig(this.getFallbackConfiguration(), updates);
      }

      // Save to localStorage
      if (this.config) {
        this.saveToLocalStorage(this.config);
        this.notifyWatchers();
      }
    } catch (error) {
      console.error('Error updating configuration:', error);
      throw error;
    }
  }

  /**
   * Merge configuration updates
   */
  private mergeConfig(current: CustomerConfiguration, updates: Record<string, any>): CustomerConfiguration {
    const merged = JSON.parse(JSON.stringify(current)); // Deep clone to avoid mutations
    
    console.log('🔀 Merging configuration updates:', updates);
    
    Object.keys(updates).forEach(key => {
      // Handle colon-separated keys (e.g., "AI:DocumentIntelligence:Models:DEED")
      if (key.includes(':')) {
        console.log('🔑 Processing colon-separated key:', key, '=', updates[key]);
        const parts = key.split(':');
        let target: any = merged;
        
        // Navigate/create nested structure
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i].charAt(0).toLowerCase() + parts[i].slice(1); // Convert to camelCase
          console.log('  📂 Navigating to:', part);
          if (!target[part]) {
            target[part] = {};
            console.log('  ✨ Created new object at:', part);
          }
          target = target[part];
        }
        
        // Set the final value
        const lastPart = parts[parts.length - 1];
        console.log('  ✅ Setting', lastPart, '=', updates[key]);
        target[lastPart] = updates[key];
      } else if (typeof updates[key] === 'object' && updates[key] !== null && !Array.isArray(updates[key])) {
        // Handle nested object updates
        console.log('🔑 Processing nested object key:', key);
        merged[key as keyof CustomerConfiguration] = {
          ...merged[key as keyof CustomerConfiguration] as any,
          ...updates[key]
        };
      } else {
        // Handle simple key-value updates
        console.log('🔑 Processing simple key:', key, '=', updates[key]);
        (merged as any)[key] = updates[key];
      }
    });
    
    console.log('✅ Merged configuration:', merged);
    return merged;
  }

  /**
   * Test configuration without saving
   */
  async testConfiguration(testConfig: Record<string, any>, sampleDocument?: any): Promise<any> {
    try {
      const response = await authFetch('/configuration/test', {
        method: 'POST',
        body: JSON.stringify({ testConfig, sampleDocument })
      });

      if (!response.ok) {
        throw new Error(`Failed to test configuration: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.testResults;
      } else {
        throw new Error(data.error || 'Failed to test configuration');
      }
    } catch (error) {
      console.error('Error testing configuration:', error);
      throw error;
    }
  }

  /**
   * Subscribe to configuration changes
   */
  onConfigurationChange(callback: (config: CustomerConfiguration) => void): () => void {
    const watcher: ConfigurationWatcher = { callback };
    this.watchers.push(watcher);

    // Return unsubscribe function
    return () => {
      const index = this.watchers.indexOf(watcher);
      if (index > -1) {
        this.watchers.splice(index, 1);
      }
    };
  }

  /**
   * Invalidate the in-memory configuration cache so the next getConfiguration()
   * call re-fetches from the server. Call this after any successful PUT/POST that
   * mutates tenant configuration (e.g. analyzer metadata save) to ensure the UI
   * reflects persisted state without a page refresh.
   */
  invalidate(): void {
    this.config = null;
  }

  /**
   * Refresh configuration manually
   */
  async refreshConfiguration(): Promise<void> {
    await this.loadConfiguration();
  }

  /**
   * Debug method to check configuration state
   */
  debugConfiguration(): void {
    console.log('🔍 Configuration Debug Info:');
    console.log('Customer ID:', this.customerId);
    console.log('Current Config:', this.config);
    console.log('LocalStorage Key:', `idp-config-${this.customerId}`);
    console.log('LocalStorage Value:', localStorage.getItem(`idp-config-${this.customerId}`));
  }

  /**
   * Notify configuration watchers
   */
  private notifyWatchers(): void {
    if (!this.config) return;

    this.watchers.forEach(watcher => {
      try {
        watcher.callback(this.config!);
      } catch (error) {
        console.error('Configuration watcher error:', error);
      }
    });
  }

  /**
   * Save configuration to localStorage
   */
  private saveToLocalStorage(config: CustomerConfiguration): void {
    try {
      const key = `idp-config-${this.customerId}`;
      localStorage.setItem(key, JSON.stringify(config));
      console.log('💾 Configuration saved to localStorage with key:', key);
      console.log('💾 Customer ID:', this.customerId);
    } catch (error) {
      console.error('Failed to save configuration to localStorage:', error);
    }
  }

  /**
   * Load configuration from localStorage
   */
  private loadFromLocalStorage(): CustomerConfiguration | null {
    try {
      const key = `idp-config-${this.customerId}`;
      console.log('📖 Loading configuration from localStorage with key:', key);
      console.log('📖 Customer ID:', this.customerId);
      const stored = localStorage.getItem(key);
      if (stored) {
        console.log('✅ Configuration found in localStorage');
        return JSON.parse(stored) as CustomerConfiguration;
      } else {
        console.log('⚠️ No configuration found in localStorage for key:', key);
      }
    } catch (error) {
      console.error('Failed to load configuration from localStorage:', error);
    }
    return null;
  }

  /**
   * Get fallback configuration
   */
  private getFallbackConfiguration(): CustomerConfiguration {
    return {
      customer: {
        id: this.customerId,
        name: 'Document Processing',
        resourceGroup: ''
      },
      ui: {
        applicationName: 'GovIQ Clerk',
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        logoUrl: '/assets/logo.svg'
      },
      ai: {
        documentIntelligence: {
          modelId: `${this.customerId}-deed-extraction-v1`,
          models: {
            DEED: `${this.customerId}-deed-extraction-v1`,
            MORTGAGE_RELEASE: `${this.customerId}-mortgage-release-extraction-v1`
          }
        },
        openai: {
          modelName: '', // MUST be set by user in Settings
          temperature: 0.1,
          maxTokens: 150,
          confidenceThreshold: 0.8
        },
        prompts: {
          ownershipTypeInference: {
            systemPrompt: 'You are an expert in real estate law. Analyze legal descriptions and determine ownership types. Return only one of these exact values: JOINT TENANCY, TENANTS IN COMMON, SOLE OWNERSHIP, COMMUNITY PROPERTY, TRUST, CORPORATE, LLC, or UNKNOWN.',
            userPromptTemplate: 'Analyze this legal description and determine the ownership type: "{legalDescription}"',
            temperature: 0.1,
            maxTokens: 150
          }
        }
      },
      processing: {
        fieldConsolidation: {
          enabled: true,
          saleDate: {
            confidenceThreshold: 0.6
          },
          legalDescription: {
            concatenationRule: 'smart_join',
            deduplication: true,
            formatting: 'normalize',
            useHighestConfidenceCoordinates: true
          }
        },
        textFormatting: {
          enabled: true,
          rules: [
            {
              fieldName: 'grantor',
              case: 'uppercase',
              trimTrailing: [',', '.', ';'],
              removePatterns: []
            },
            {
              fieldName: 'grantee',
              case: 'uppercase', 
              trimTrailing: [',', '.', ';'],
              removePatterns: []
            },
            {
              fieldName: 'legalDescription',
              case: 'preserve',
              trimTrailing: [',', '.', ';'],
              removePatterns: ['\\s+', '\\|']
            }
          ]
        }
      },
      integration: {
        systemOfRecord: {
          apiEndpoint: '',
          authType: 'apikey'
        },
        transactionTypes: ['Gift', 'Trust', 'Regular'],
        ownershipTypes: ['Gift', 'Deed', 'Trust']
      },
      limits: {
        documentsPerMonth: 10000,
        storageGB: 100,
        maxFileSize: '50MB',
        pricing: {
          perDocument: 0.05,
          perGB: 0.25,
          baseMonthly: 150
        }
      }
    };
  }
}

// Singleton instance
const configurationService = new ConfigurationService();

export default configurationService;
export type { CustomerConfiguration };